import { Release } from '../../../../../common/types/release/release';
import { PlanNames } from '../../../../../services/product/types/plan-names';
import AppState from '../state';
import {
  getCalendarTiers,
  getPriceTier,
  CalendarTiers,
  getFeaturePrice,
} from '../../sdk/plans';
import moment from 'moment';
import { Order } from '@webapp/types/orders/order';
import SimpleTrack from '@common/types/release/simple-track';
import { ExtraFeaturesName } from '@services/product/types/extra-features-name';
import { ExtraFeaturesKey } from '@services/product/types/extra-features-key';
import { ReleaseStatus } from '../../../../../common/types/release/relase-status';
import {
  Collabrators,
  SimpleRelease,
} from '../../../../../common/types/release/simple-release';
import CatalogRelease from '../../../../../common/types/catalog/catalog-release';
import { MerchProductStatus } from '@services/merch/types/api/merch-product-status';

export declare type ProductsInfo = {
  name: ExtraFeaturesName;
  description: string;
  key: ExtraFeaturesKey;
  getProductPrice: (data: {
    plan?: PlanNames;
    state?: AppState;
    force?: boolean;
    isActive: boolean;
  }) => { name: string; price: number; quantity?: number } | null;
};

const getCalendarCost = (
  release?: Partial<Release>
): CalendarTiers['priceTiers'][0] | undefined => {
  const tiers = getCalendarTiers(release?.trackCount || 1);
  const lastPaidAmount =
    release?.paidItems?.find((item) => isReleaseDate(item.product.id))?.price
      .value || 0;

  if (releaseDateWasPaid(release)) {
    return;
  }

  if (tiers) {
    const releaseDate = moment(release?.releaseDate);
    const tier = getPriceTier(releaseDate, tiers.priceTiers);
    const total =
      tier && tier?.price > lastPaidAmount ? tier?.price - lastPaidAmount : 0;

    return tier
      ? {
          upto: tier.upto,
          name: tier.name,
          price: total,
        }
      : undefined;
  }
};

const isInstantGratification = (key: string) =>
  key === 'RELEASE.INSTANT_GRATIFICATION';
const isReleaseDate = (key: string) => key === 'RELEASE.DATE';

export const releaseDateWasPaid = (release?: Partial<Release>) => {
  return (
    // release?.backofficeStatus !== undefined ||
    !!release?.submitDate &&
    moment(release.submitDate).isSame(moment(release.releaseDate))
  );
};

const getQuantity = (
  product: ProductsInfo,
  release?: Partial<Release>,
  tracks?: Partial<SimpleTrack>[]
) => {
  if (isInstantGratification(product.key)) {
    const paid = release?.paidItems?.find((item) =>
      isInstantGratification(item.product.id)
    );
    const paidCount = paid ? paid.quantity : 0;

    const count = tracks
      ? tracks.filter((track) => track.isInstantGratification).length
      : 0;
    const total = count <= paidCount ? 0 : count - paidCount;
    return total;
  }
  return 1;
};

const wasPaidFor = (
  product: ProductsInfo,
  release?: Partial<Release>,
  tracks?: Partial<SimpleTrack>[]
) => {
  const exists = (release?.paidItems || []).find((item) => {
    if (isInstantGratification(product.key)) {
      return (
        item.product.id === product.key &&
        !(getQuantity(product, release, tracks) > 0)
      );
    }
    if (isReleaseDate(product.key) && !releaseDateWasPaid(release)) {
      return undefined;
    }
    return item.product.id === product.key;
  });

  return !!exists;
};

export const releaseDatePassed = (
  key: ExtraFeaturesKey,
  releaseDate?: string
) => {
  if (!releaseDate) {
    return false;
  }

  if (
    ![
      'RELEASE.DATE',
      'RELEASE.PRE_ORDER',
      'RELEASE.INSTANT_GRATIFICATION',
    ].includes(key)
  ) {
    return false;
  }

  const hasPassed = moment().isSameOrAfter(moment(releaseDate), 'day');

  return hasPassed;
};

const getProductOrder = (data: {
  name: ExtraFeaturesName;
  description: string;
  price: number;
  productName: string;
  key: ExtraFeaturesKey;
  quantity: number;
}): Order => {
  return {
    product: {
      category: 'RELEASE',
      name: data.name,
      caption: '',
      description: data.description,
      prices: [],
      id: data.key,
      product_key: data.key,
      created_at: new Date(),
      updated_at: new Date(),
    },
    price: {
      id: data.name,
      value: data.price,
      currency: 'USD',
      recurrent: false,
      category: 'RELEASE',
      productId: `${data.key}.${data.productName}`,
      created_at: new Date(),
      updated_at: new Date(),
    },
    quantity: data.quantity,
  };
};

const getTracks = (state?: AppState) =>
  state
    ? Object.keys(state.tracks.tracks)
        .filter(
          (key) =>
            state.tracks.tracks[key].releaseId === state.release.release?.id
        )
        .map((key) => state.tracks.tracks[key])
    : [];

export const PRODUCTS_INFO: ProductsInfo[] = [
  {
    key: 'RELEASE.PAY_AS_YOU_GO',
    name: 'Pay as You Go',
    description: 'Pay As You Go',
    getProductPrice: ({ plan, state, isActive }) => {
      return getFeaturePrice(
        'RELEASE.PAY_AS_YOU_GO',
        false,
        plan,
        isActive,
        getTracks(state).length
      );
    },
  },
  {
    key: 'RELEASE.DATE',
    name: 'Speedy Release',
    description: 'Speedy Release',
    getProductPrice: ({ state }) =>
      getCalendarCost(state?.release.release) || null,
  },
  {
    key: 'RELEASE.STORE_MAXIMIZER',
    name: 'Stores Plus',
    description: 'Stores +',
    getProductPrice: ({ plan, state, isActive }) =>
      getFeaturePrice(
        'RELEASE.STORE_MAXIMIZER',
        state?.release.release?.isStoreMaximizer || false,
        plan,
        isActive
      ),
  },
  {
    key: 'RELEASE.PRE_ORDER',
    name: 'Pre Order',
    description: 'Pre Order',
    getProductPrice: ({ plan, state, isActive }) =>
      getFeaturePrice(
        'RELEASE.PRE_ORDER',
        state?.release.release?.hasPreorder || false,
        plan,
        isActive
      ),
  },
  {
    key: 'RELEASE.INSTANT_GRATIFICATION',
    name: 'Instant Gratification',
    description: 'Instant Gratification',
    getProductPrice: ({ plan, isActive }) =>
      getFeaturePrice('RELEASE.INSTANT_GRATIFICATION', true, plan, isActive),
  },
  {
    key: 'RELEASE.YOUTUBE_CONTENT_ID',
    name: 'YouTube Content ID',
    description: 'YouTube Content ID',
    getProductPrice: ({ plan, state, force, isActive }) =>
      getFeaturePrice(
        'RELEASE.YOUTUBE_CONTENT_ID',
        force || state?.release.release?.hasYoutubeContentID || false,
        plan,
        isActive,
        getTracks(state).length
      ),
  },
  {
    key: 'RELEASE.APPLE_DISTRIBUTION',
    name: 'Apple Music Distribution',
    description: 'Publish to Apple Music',
    getProductPrice: ({ plan, state, force, isActive }) =>
      getFeaturePrice(
        'RELEASE.APPLE_DISTRIBUTION',
        force || state?.release.release?.hasAppleMusic || false,
        plan,
        isActive,
        getTracks(state).length
      ),
  },
  {
    key: 'RELEASE.REMOVE_BRANDING',
    name: 'Remove Branding',
    description: 'Remove Branding',
    getProductPrice: ({ plan, state, isActive }) =>
      getFeaturePrice(
        'RELEASE.REMOVE_BRANDING',
        state?.release.release?.removeBranding || false,
        plan,
        isActive
      ),
  },
];

export const releaseCheckout = (state: AppState): Order[] => {
  const basket: Order[] = [];
  const plan =
    state.subscription.current?.plan.toLocaleLowerCase() as PlanNames;
  const isActive = !!state.subscription.current?.isActive;
  const release = state.release.release;
  const tracks = getTracks(state);

  PRODUCTS_INFO.forEach((product) => {
    if (
      !wasPaidFor(product, release, tracks) &&
      !releaseDatePassed(product.key, release?.releaseDate)
    ) {
      const priceData = product.getProductPrice({ plan, state, isActive });
      if (priceData) {
        const quantity = getQuantity(product, state.release.release, tracks);
        if (quantity > 0) {
          const order = getProductOrder({
            name: product.name,
            key: product.key,
            description: product.description,
            price: priceData.price,
            productName: priceData.name,
            quantity,
          });
          if (order) {
            basket.push(order);
          }
        }
      }
    }
  });

  return basket;
};

export const activeSummary = (state: AppState) => {
  return state.release.summary[state.release.activeSection];
};

export const activeList = (state: AppState) => {
  return state.release.list[state.release.activeSection];
};

export const hasCheckoutItems = (state: AppState) => {
  const plan =
    state.subscription.current?.plan.toLocaleLowerCase() as PlanNames;
  const isActive = !!state.subscription.current?.isActive;
  const release = state.release.release;
  const tracks = Object.keys(state.tracks.tracks).map(
    (key) => state.tracks.tracks[key]
  );

  return PRODUCTS_INFO.some((product) => {
    let status = false;
    if (
      !wasPaidFor(product, release, tracks) &&
      !releaseDatePassed(product.key, release?.releaseDate)
    ) {
      status = !!product.getProductPrice({ plan, state, isActive });
    }
    return status;
  });
};

export const getStatusLabel = (
  status?: ReleaseStatus,
  releaseDate?: string | Date,
  approved?: boolean
) => {
  const isPublished = moment().isSameOrAfter(moment(releaseDate));

  switch (status) {
    case 'draft':
      return 'Draft';

    case 'pending':
    case 'pending-processing':
      return 'Pending';

    case 'taken-down':
      return 'Taken Down';
    case 'changes-needed':
      return 'Changes Needed';
    case 'approved':
      return 'Approved';
    case 'released':
      return 'Released';
    case 'rejected':
      return 'Rejected';
    case 'pending-takedown':
      return 'Pending Takedown';
    case 'published':
      if (isPublished) {
        if (approved || approved === undefined) {
          return 'Released';
        }
        return 'Partially Released';
      } else {
        if (approved || approved === undefined) {
          return 'Approved';
        }
        return 'Partially Approved';
      }
    default:
      return 'Draft';
  }
};

export const getStatusLabelMerch = (
  status?: MerchProductStatus,
  releaseDate?: string | Date,
  approved?: boolean
) => {
  const isPublished = moment().isSameOrAfter(moment(releaseDate));

  switch (status) {
    case 'draft':
      return 'Draft';
    case 'processing':
      return 'Pending';
    case 'published':
      if (isPublished) {
        if (approved || approved === undefined) {
          return 'Published';
        }
        return 'Partially Published';
      } else {
        if (approved || approved === undefined) {
          return 'Approved';
        }
        return 'Partially Approved';
      }
    default:
      return 'Draft';
  }
};

export const getMergedMastersAndFeatured = (
  state: AppState
): CatalogRelease[] => {
  const featuredItems =
    state.release.featuring
      ?.filter(
        (f) =>
          !state.catalog.master.items.find((m) => m.id === f.id)?.id &&
          state.artist.active?.id !== f.ownerId
      )
      .map(
        (release): CatalogRelease => ({
          id: release.id,
          created_at: new Date(release.created_at),
          updated_at: new Date(release.updated_at),
          title: release.title,
          artist: {
            id: state.artist.active?.id as string,
            name: state.artist.active?.name as string,
            avatar: state.artist.active?.avatar,
          },
          trackCount: release.trackCount,
          ownerId: state.user.profile?.id as string,
          status: release.status,
          split: {
            owner: 0,
            writer: 0,
          },
          // TODO: DEVTEAM-603 - FOR DEMO ONLY - We need to have the tracks from each realease here.
          tracks:
            state.catalog.master.items.find((r) => r.id === release.id)
              ?.tracks || [],
          upc: release.UPCEAN,
          cover_url: release.cover.url,
          release_date: new Date(release.releaseDate || new Date()),
          approved: release.approved,
          tracLink: release.traclink || '',
          featuring: true,
        })
      ) || [];
  return [...featuredItems, ...(state.catalog.master.items || [])];
};

export const getAllReleasesAsCatalog = (state: AppState): CatalogRelease[] => {
  const featuredReleases =
    state.release.featuring?.filter(
      (f) => !state.release.summary?.all?.list?.some((r) => r.id === f.id)
    ) || [];
  return ([] as SimpleRelease[])
    .concat(state.release.summary?.all?.list || [], featuredReleases)
    .map(
      (release): CatalogRelease => ({
        id: release.id,
        created_at: new Date(release.created_at),
        updated_at: new Date(release.updated_at),
        title: release.title,
        artist: {
          id: state.artist.active?.id as string,
          name: state.artist.active?.name as string,
          avatar: state.artist.active?.avatar,
        },
        trackCount: release.trackCount,
        ownerId: state.user.profile?.id as string,
        status: release.status,
        featuring: featuredReleases.some((r) => r.id === release.id),
        split: {
          owner: 0,
          writer: 0,
        },
        tracks:
          state.catalog.master.items.find((r) => r.id === release.id)?.tracks ||
          state.release.summary.all?.list
            .find((r) => r.id === release.id)
            ?.tracks?.map((track) => {
              return {
                ...track,
                name: track.title,
                split: {
                  owner: 0,
                  writer: 0,
                },
                order: track.order || 0,
                collaborators: [],
              };
            }) ||
          [],
        upc: release.UPCEAN,
        cover_url: release.cover.url,
        release_date: new Date(release.releaseDate || new Date()),
        approved: release.approved,
        tracLink: release.traclink,
      })
    );
};

export const getMergedReleaseListSummary = (
  state: AppState
): CatalogRelease[] => {
  return getAllReleasesAsCatalog(state).map((list: CatalogRelease) => {
    let collaborators: Collabrators[] = [];
    let takedownTime = '';
    state.release.summary?.all?.list?.forEach((summary: SimpleRelease) => {
      if (list.id === summary.id && summary.collabrators) {
        collaborators = summary.collabrators;
      }
      if (list.id === summary.id && summary.takedownTime) {
        takedownTime = summary.takedownTime;
      }
    });
    if (collaborators?.length && takedownTime) {
      return { ...list, collaborators, takedownTime };
    }
    if (collaborators?.length) {
      return { ...list, collaborators };
    }
    if (takedownTime) {
      return { ...list, takedownTime };
    }
    return list;
  });
};

export const blockEditor = (state: AppState) =>
  state.release.release?.isPublished &&
  (state.release.release?.status === 'approved' ||
    state.release.release?.status === 'released' ||
    state.release.release?.status === 'pending-processing' ||
    state.release.release?.status === 'published' ||
    state.release.release?.status === 'pending-takedown' ||
    state.release.release?.status === 'taken-down');
